import React, {
	type KeyboardEvent,
	type MouseEvent,
	type ReactNode,
	useEffect,
	useState,
} from 'react';

import { useIntl } from 'react-intl-next';
import { di } from 'react-magnetic-di';

import { type UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button, { IconButton } from '@atlaskit/button/new';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import Heading from '@atlaskit/heading';
// import PersonIcon from '@atlaskit/icon/core/person';
// import FavouritesIcon from '@atlaskit/icon/core/star-unstarred';
// import LikesIcon from '@atlaskit/icon/core/thumbs-up';
import ChevronLeftLargeIcon from '@atlaskit/icon/glyph/chevron-left-large';
import CopyIcon from '@atlaskit/icon/glyph/copy';
import MoreIcon from '@atlaskit/icon/glyph/more';
import Modal, { ModalBody, ModalHeader, ModalTitle } from '@atlaskit/modal-dialog';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Grid, Inline, Stack, Text, xcss } from '@atlaskit/primitives';
import { Card as SmartCard, Provider as SmartCardProvider } from '@atlaskit/smart-card';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import {
	AnalyticsContextWithSourceAndProperties,
	InteractionSource,
	useAnalytics,
} from '@atlassian/conversation-assistant-instrumentation';
import { type Agent } from '@atlassian/conversation-assistant-service';
import { useUserPreferences } from '@atlassian/conversation-assistant-store';
import { ChatPillIcon, StarIconButton } from '@atlassian/conversation-assistant-ui-components';

import { AgentConversationStarters } from '../../../common/ui/agent-conversation-starters';
import { useAgentsController } from '../../../controllers/agents';
import { useTenantDataController } from '../../../controllers/tenant-data';
import { AgentCreatedBy } from '../../../utils/agents';

import messages from './messages';

const modalBodyStyles = xcss({
	paddingBottom: 'space.300',
	height: '100%',
});

const instructionsContainerStyles = xcss({
	border: `1px solid ${token('color.border.input')}`,
	borderRadius: 'border.radius.100',
	overflowY: 'auto',
	whiteSpace: 'pre-line',
	position: 'relative',
	maxHeight: '100%',
	padding: 'space.100',
	flex: '1',
});

const copyContainerStyles = xcss({
	position: 'absolute',
	top: '10px',
	right: '10px',
});

const chatToAgentButtonContainer = xcss({
	width: '100%',
});

const chatToAgentButtonWrapper = xcss({
	display: 'flex',
	justifyContent: 'center',
	lineHeight: '20px',
	padding: 'space.075',
	fontWeight: '500',
});

const chatPillButtonInlineStyles = xcss({ paddingInline: 'space.025' });

const chatPillTextStyles = xcss({
	wordBreak: 'break-word',
	textAlign: 'left',
	whiteSpace: 'pre-wrap',
});

const chatPillIconWrapper = xcss({
	minWidth: '20px',
	height: '20px',
});

const conversationStartersStyles = xcss({
	marginBottom: 'space.200',
});

const clickableItemStyles = xcss({
	display: 'flex',
	alignItems: 'center',
});

const createdByTextStyles = xcss({
	paddingRight: 'space.050',
});

type ViewAgentModalProps = {
	agent: Agent;
	siteId: string;
	onClose: () => void;
	onGoBack: () => void;
	onClickChat: (agent: Agent) => void;
	handleClickConversationStarter: (agent: Agent, prompt: string) => void;
	isAgentCreatedByUser: boolean;
	onEditAgent: (
		e: MouseEvent<Element, globalThis.MouseEvent> | KeyboardEvent<Element>,
		agentId: string,
	) => void;
	onCopyAgent: (
		e: MouseEvent<Element, globalThis.MouseEvent> | KeyboardEvent<Element>,
		agentId: string,
	) => void;
	onDuplicateAgent: (
		e: MouseEvent<Element, globalThis.MouseEvent> | KeyboardEvent<Element>,
		agentId: string,
	) => void;
	onDeleteAgent: (
		e: MouseEvent<Element, globalThis.MouseEvent> | KeyboardEvent<Element>,
		agentId: string,
	) => void;
};

const fallbackComponent = (url: string) => () => (
	<a href={url} target="_blank" rel="noopener noreferrer">
		{url}
	</a>
);

const displayAgentSpaceFilter = (cloudUrl: string, spaceFilters?: string[]) => {
	if (!spaceFilters?.length) {
		return null;
	}
	const spaceLinks = spaceFilters.map((spaceId) => `${cloudUrl}/wiki/spaces/${spaceId}/overview`);

	return spaceLinks.map((url: string) => (
		<SmartCard key={url} fallbackComponent={fallbackComponent(url)} appearance="inline" url={url} />
	));
};

const knowledgeWrapperStyles = xcss({
	border: `1px solid ${token('color.border')}`,
	borderRadius: '6px',
	padding: 'space.300',
	marginTop: 'space.300',
	display: 'flex',
	gap: 'space.100',
	flexDirection: 'column',
});

// const dividerStyles = xcss({
// 	width: '1px',
// 	height: '16px',
// 	backgroundColor: 'color.background.neutral.subtle.pressed',
// });

// const metadataStyles = xcss({
// 	paddingInline: 'space.200',
// });

export const ViewAgentModal = ({
	agent,
	siteId,
	onClose,
	onGoBack,
	onClickChat,
	handleClickConversationStarter,
	isAgentCreatedByUser,
	onEditAgent,
	onCopyAgent,
	onDuplicateAgent,
	onDeleteAgent,
}: ViewAgentModalProps) => {
	di(useUserPreferences, useTenantDataController);
	const [instructionsHovered, setInstructionsHovered] = useState(false);
	const [mouseEnterHappened, setMouseEnterHappened] = useState(false);

	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();
	const { setFavouriteAgent } = useAgentsController();
	const { setFavouriteAgent: setFavouriteAgentViaPrefs, isFavouriteAgent } = useUserPreferences();
	const isBEFavouritingAgentsEnabled = fg('rovo_chat_favourite_agent_via_backend');
	const isStarred = isBEFavouritingAgentsEnabled ? agent.favourite : isFavouriteAgent(agent.id);

	const agentPluginConfig = agent.available_plugins?.['Search-QA-Plugin'];
	const { data, isFetching } = useTenantDataController(siteId);
	const shouldDisplayAgentSmartCards =
		data && !isFetching && agentPluginConfig?.space_filter?.length;

	useEffect(() => {
		sendAnalyticsEvent({
			eventType: 'screen',
			name: 'viewAgentModal',
			attributes: {
				agentId: agent.id,
			},
		});
	}, [sendAnalyticsEvent, agent.id]);

	const handleGoBack = (analyticsEvent: UIAnalyticsEvent) => {
		onGoBack();
		sendAnalyticsEvent(
			analyticsEvent.update({
				actionSubjectId: 'viewAgentBackButton',
				attributes: {
					agentId: agent.id,
				},
			}),
		);
	};

	const handleCloseViewAgentModal = (analyticsEvent: UIAnalyticsEvent) => {
		onClose();
		sendAnalyticsEvent(
			analyticsEvent.update({
				actionSubjectId: 'viewAgentCloseModalButton',
				attributes: {
					agentId: agent.id,
				},
			}),
		);
	};

	const handleClickChat = (analyticsEvent: UIAnalyticsEvent) => {
		onClickChat(agent);
		sendAnalyticsEvent(
			analyticsEvent.update({
				actionSubjectId: 'viewAgentChatToAgentButton',
				attributes: {
					agentId: agent.id,
				},
			}),
		);
	};

	const handleFavouriteAgent = async (agentId: string) => {
		if (isBEFavouritingAgentsEnabled) {
			await setFavouriteAgent(agentId, !isStarred);
		} else {
			setFavouriteAgentViaPrefs(agentId, !isStarred);
		}

		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'viewAgentFavoriteAgentButton',
			attributes: {
				isFavorited: !isStarred,
				agentId: agent.id,
			},
		});
	};

	const handleClickCreatorLink = (agentId: string) => {
		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'link',
			actionSubjectId: 'viewAgentCreatorLink',
			attributes: {
				agentId,
			},
		});
	};

	const handleCopyInstructions = () => {
		if (agent.system_prompt_template) {
			navigator.clipboard.writeText(agent.system_prompt_template.toString());
		}
		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'viewAgentCopyInstructionsButton',
			attributes: {
				agentId: agent.id,
			},
		});
	};

	return (
		<SmartCardProvider>
			<AnalyticsContextWithSourceAndProperties source="AIMate">
				<Modal testId="view-agent-modal" height="100vh" width="x-large">
					<ModalHeader>
						<Inline space="space.100" alignBlock="center">
							<IconButton
								onClick={(_, analyticsEvent) => handleGoBack(analyticsEvent)}
								appearance="subtle"
								icon={ChevronLeftLargeIcon}
								label={formatMessage(messages.goBackLabel)}
							/>
							<ModalTitle>{formatMessage(messages.modalTitle)}</ModalTitle>
						</Inline>
						<Button
							appearance="subtle"
							onClick={(_, analyticsEvent) => {
								handleCloseViewAgentModal(analyticsEvent);
							}}
						>
							{formatMessage(messages.closeButton)}
						</Button>
					</ModalHeader>
					<ModalBody>
						<Grid xcss={modalBodyStyles} templateColumns="1fr 2fr" columnGap="space.300">
							<Stack space="space.100">
								<Stack>
									<Inline space="space.100" alignBlock="center">
										<Heading size="xlarge">{agent.name}</Heading>
										<StarIconButton
											isStarred={isStarred}
											handleToggle={() => handleFavouriteAgent(agent.id)}
										/>
									</Inline>
									<Box xcss={clickableItemStyles} onClick={() => handleClickCreatorLink(agent.id)}>
										{formatMessage(messages.createdByLabel, {
											text: (chunks: ReactNode[]) => <Box xcss={createdByTextStyles}>{chunks}</Box>,
											agentName: (
												<Box>
													<AgentCreatedBy agent={agent} siteId={siteId} />
												</Box>
											),
										})}
									</Box>
								</Stack>
								<Text>{agent.description}</Text>
								<Stack space="space.100">
									{/* <Flex xcss={metadataStyles} justifyContent="space-between">
										<Stack space="space.050" alignBlock="center" alignInline="center">
											<PersonIcon label={formatMessage(messages.usersMetadataLabel)} />
											<Text align="center" color="color.text.subtle" size="small">
												388 users
											</Text>
										</Stack>
										<Box xcss={dividerStyles} />
										<Stack space="space.050" alignInline="center">
											<LikesIcon label={formatMessage(messages.reactionsMetadataLabel)} />
											<Text color="color.text.subtle" size="small">
												1.2k reactions!
											</Text>
										</Stack>
										<Box xcss={dividerStyles} />
										<Stack space="space.050" alignInline="center">
											<FavouritesIcon label={formatMessage(messages.favouritesMetadataLabel)} />
											<Text color="color.text.subtle" size="small">
												129 starred
											</Text>
										</Stack>
									</Flex> */}
									<Inline space="space.100">
										<Box xcss={chatToAgentButtonContainer}>
											<Button
												shouldFitContainer
												onClick={(_, analyticsEvent) => handleClickChat(analyticsEvent)}
											>
												<Box xcss={chatToAgentButtonWrapper}>
													<Inline space="space.050" xcss={chatPillButtonInlineStyles}>
														<Box xcss={chatPillIconWrapper}>
															<ChatPillIcon />
														</Box>
														<Box xcss={chatPillTextStyles}>
															{formatMessage(messages.chatToAgentButton)}
														</Box>
													</Inline>
												</Box>
											</Button>
										</Box>
										<DropdownMenu<HTMLButtonElement>
											trigger={({ triggerRef, ...props }) => (
												<IconButton
													{...props}
													icon={MoreIcon}
													label={formatMessage(messages.moreActionsLabel)}
													ref={triggerRef}
												/>
											)}
										>
											<DropdownItemGroup>
												<DropdownItem onClick={(e) => onDuplicateAgent(e, agent.id)}>
													{formatMessage(messages.duplicateAgent)}
												</DropdownItem>
												<DropdownItem onClick={(e) => onCopyAgent(e, agent.id)}>
													{formatMessage(messages.copyLinkToProfile)}
												</DropdownItem>
											</DropdownItemGroup>
											{isAgentCreatedByUser ? (
												<DropdownItemGroup hasSeparator>
													<DropdownItem onClick={(e) => onEditAgent(e, agent.id)}>
														{formatMessage(messages.editAgent)}
													</DropdownItem>
													<DropdownItem onClick={(e) => onDeleteAgent(e, agent.id)}>
														{formatMessage(messages.deleteAgent)}
													</DropdownItem>
												</DropdownItemGroup>
											) : null}
										</DropdownMenu>
									</Inline>
								</Stack>
								{!!shouldDisplayAgentSmartCards && (
									<Box xcss={knowledgeWrapperStyles}>
										<Text size="small" color="color.text.subtle" weight="semibold">
											{formatMessage(messages.knowledgeLabel)}
										</Text>
										<Stack space="space.050">
											{displayAgentSpaceFilter(data.cloudUrl, agentPluginConfig?.space_filter)}
										</Stack>
									</Box>
								)}
							</Stack>
							<Stack space="space.050">
								<Stack alignInline="start" space="space.100" xcss={conversationStartersStyles}>
									<Text size="small" color="color.text.subtle" weight="semibold">
										{formatMessage(messages.openChatLabel)}
									</Text>
									<Stack space="space.100">
										<AgentConversationStarters
											agent={agent}
											interactionSource={InteractionSource.VIEW_AGENT_CONVERSATION_STARTERS}
											onConversationStarterClick={(conversationStarter) => {
												handleClickConversationStarter(agent, conversationStarter);
												onClose();
											}}
										/>
									</Stack>
								</Stack>

								<Stack space="space.100" grow="fill">
									<>
										<Text size="small" color="color.text.subtle" weight="semibold">
											{formatMessage(messages.instructionsLabel)}
										</Text>
										<Box
											xcss={instructionsContainerStyles}
											onMouseEnter={() => {
												setMouseEnterHappened(true);
												setInstructionsHovered(true);
											}}
											onMouseLeave={() => {
												setMouseEnterHappened(false);
												setInstructionsHovered(false);
											}}
											onFocus={() => setInstructionsHovered(true)}
											onBlur={() => {
												if (!mouseEnterHappened) {
													setInstructionsHovered(false);
												}
											}}
										>
											{agent.system_prompt_template ? (
												<>
													<Text>{agent.system_prompt_template}</Text>
													{instructionsHovered && (
														<Box xcss={copyContainerStyles}>
															<Tooltip content="Copy response" position="top">
																<IconButton
																	type="button"
																	onClick={() => handleCopyInstructions()}
																	icon={(iconProps) => <CopyIcon {...iconProps} />}
																	appearance="subtle"
																	spacing="compact"
																	label={formatMessage(messages.copyInstructionsLabel)}
																/>
															</Tooltip>
														</Box>
													)}
												</>
											) : null}
										</Box>
									</>
								</Stack>
							</Stack>
						</Grid>
					</ModalBody>
				</Modal>
			</AnalyticsContextWithSourceAndProperties>
		</SmartCardProvider>
	);
};
